// medicationUtils.ts

import { Drip, Drug, DrugDetails } from "../Cpr/types";

interface DosageValues {
  primaryValue: string;
  mlValue: string;
}

export const formatNumber = (num: number): string => {
  let formatted = num.toFixed(2);
  formatted = formatted.replace(/\.?0+$/, '');
  return formatted;
};

export const splitRatio = (ratio: string | undefined): number[] => {
  if (!ratio) return [];
  return ratio.split('/').map(Number);
};

// Drug-specific utilities
export const getAdministrationUnit = (drugDefinition: DrugDetails): string => {
  if (drugDefinition.type === 'mass') {
    return drugDefinition.dose_unit || '';
  } else {
    return 'ml';
  }
};

export const getDoseByWeightWithMaxLimit = (drugDefinition: DrugDetails, weight: number | null): number => {
  if (drugDefinition.constant_dose) {
    return drugDefinition.constant_dose;
  }
  if (weight === null) return 0;
  const dose_per_kg = drugDefinition.dose_per_kg ?? 0;
  let doseByWeight = dose_per_kg * weight;
  if (drugDefinition.maxDose) {
    doseByWeight = Math.min(Number(drugDefinition.maxDose), doseByWeight);
  }
  if (drugDefinition.minDose) {
    doseByWeight = Math.max(Number(drugDefinition.minDose), doseByWeight);
  }
  return doseByWeight;
};

export const getDoseByWeightWithMaxLimitFormatted = (drugDefinition: DrugDetails, weight: number | null): string => {
  return formatNumber(getDoseByWeightWithMaxLimit(drugDefinition, weight));
};

export const calcVolume = (drugDefinition: DrugDetails, weight: number | null): number => {
  const doseByWeight = getDoseByWeightWithMaxLimit(drugDefinition, weight);
  const [numerator, denominator] = splitRatio(drugDefinition.concentration);
  const concentration = numerator / denominator;
  return doseByWeight / concentration;
};

export const calcAmountToAdminister = (drugDefinition: Drug, weight: number | null): string => {
  let amount;
  if (drugDefinition.type === 'fluid' || drugDefinition.type === 'mass') {
    amount = getDoseByWeightWithMaxLimit(drugDefinition, weight);
  } else {
    amount = calcVolume(drugDefinition, weight);
  }
  return formatNumber(amount);
};

// Drip-specific utilities
export const calcDosePerHourPerWeight = (drugData: Drip, weight: number | null): number => {
  if (weight === null) return 0;
  let drug_per_hour = 0;
  if (drugData.dose_per_kg_per_min || drugData.dose_per_kg_per_min === 0) {
    drug_per_hour = drugData.dose_per_kg_per_min * 60;
  } else if (drugData.dose_per_kg_per_hour || drugData.dose_per_kg_per_hour === 0) {
    drug_per_hour = drugData.dose_per_kg_per_hour;
  } else {
    console.error("neither minute nor hour provided to drug " + drugData.name);
    return 0;
  }
  if (drugData.max_dose_per_hour) {
    return Math.min(drug_per_hour * weight, drugData.max_dose_per_hour);
  }
  else {
    return drug_per_hour * weight;
  }
};

export const dripUsesMaxDose = (drugData: Drip, weight: number | null): boolean => {
  if (!weight || !drugData.max_dose_per_hour) {
    return false;
  }
  return calcDosePerHourPerWeight(drugData, weight) == drugData.max_dose_per_hour;
}

export const prettifyUnits = (dose: number, units: string | undefined): { dose: number; units: string | undefined } => {
  if (dose < 1000) {
    return { dose, units };
  } else {
    if (units === 'mcg') {
      return { dose: dose / 1000, units: 'mg' };
    }
    return { dose, units };
  }
};

export const getTargetVolumePerHour = (drip: Drip): number => {
  return drip.target_volume_ml_per_hour ?? 0;
};

export const calcDilutionPerKg = (drip: Drip, weight: number | null): {
  doseBeforeDilution: string;
  unitsBeforeDilution: string | undefined;
  doseForDilution: string;
  unitsForDilution: string | undefined;
} => {
  const dosePerHour = calcDosePerHourPerWeight(drip, weight);
  const dose_to_add = ((drip.default_dilution_volume_ml ?? 0) / getTargetVolumePerHour(drip)) * dosePerHour;
  const { dose: doseForDilution, units: unitsForDilution } = prettifyUnits(dose_to_add, drip.dose_unit);
  const { dose: doseBeforeDilution, units: unitsBeforeDilution } = prettifyUnits(dosePerHour, drip.dose_unit);

  return {
    doseBeforeDilution: formatNumber(doseBeforeDilution),
    unitsBeforeDilution,
    doseForDilution: formatNumber(doseForDilution),
    unitsForDilution
  };
};

export const calcDripDosageByMl = (mlValue: number, drip: Drip, weight: number): string => {
  if (!drip.existing_dilution_concentration ) {
    console.error("Currently we don't support dosage by ml for non InfusionSpeed drips. drip with error:" + drip.name);
    return '';
  }
  const [numerator, denominator] = drip.existing_dilution_concentration.split('/').map(Number);
  const weightNormalized = (weight || NaN) * (drip.dose_per_kg_per_min ? 60 : 1);
  return formatNumber((mlValue * numerator / denominator) / weightNormalized);
};

export const calcInfusionSpeed = (drip: Drip, weight: number | null): string => {
  const dosePerKg = calcDosePerHourPerWeight(drip, weight);
  if (drip.dose_unit !== drip.existing_dilution_concentration_dose_unit) {
    console.error("Dosage unit and existing concentration does not match. need to implement units alignment before calculation. drug with error:" + drip.name);
    return '';
  }
  if (drip.existing_dilution_concentration) {
    const [numerator, denominator] = drip.existing_dilution_concentration.split('/').map(Number);
    const concentration = numerator / denominator;
    return formatNumber(dosePerKg / concentration);  // Volume = Mass / Concentration
  }
  console.error("Missing existing dilution concentration for drug: " + drip.name);
  return '';
};

export const getDripRate = (drip: Drip): number | undefined => {
  return drip.dose_per_kg_per_min ?? drip.dose_per_kg_per_hour;
};

export const getTimeUnitString = (drip: Drip): string => {
  return drip.dose_per_kg_per_min ? 'minute' : 'hour';
};

export const createDrugWithNewDosePerKg = (
  originalDrug: Drug,
  newDoseValue: number,
  weight: number | null
): Drug => {
  if (!weight) return originalDrug;

  return {
    ...originalDrug,
    dose_per_kg: newDoseValue / weight
  };
};

export const administrationUnitToDosage = (drug: Drug, weight: number | null, newValue: string): DosageValues => {
  if (!weight || newValue === '') {
    return {
      primaryValue: '',
      mlValue: ''
    };
  }

  try {
    const numericValue = parseFloat(newValue);

    if (isNaN(numericValue)) {
      return {
        primaryValue: '',
        mlValue: ''
      };
    }

    // Case 1: Primary value (dose_unit) was changed
    const newDrug = createDrugWithNewDosePerKg(drug, numericValue, weight);
    const mlValue = calcAmountToAdminister(newDrug, weight);

    return {
      primaryValue: newValue,
      mlValue
    };
  } catch (error) {
    console.error('Error in administrationUnitToDosage:', error);
    return {
      primaryValue: '',
      mlValue: ''
    };
  }
};

export const mlToDosage = (drug: Drug, newMlValue: string): DosageValues => {
  if (newMlValue === '') {
    return {
      primaryValue: '',
      mlValue: ''
    };
  }

  try {
    const numericValue = parseFloat(newMlValue);

    if (isNaN(numericValue)) {
      return {
        primaryValue: '',
        mlValue: ''
      };
    }

    const [numerator, denominator] = splitRatio(drug.concentration);
    const primaryValue = ((numerator * numericValue) / denominator).toString();

    return {
      primaryValue,
      mlValue: newMlValue
    };
  } catch (error) {
    console.error('Error in mlToDosage:', error);
    return {
      primaryValue: '',
      mlValue: ''
    };
  }
};